import {
  SB,
  createSharedStateComputedProperties,
  matchableString,
  payloadComputedProperty,
  searchableComputedProperty,
} from '@play-co/replicant';
import { teamProfileSchema, teamSharedStateSchema } from './team.schema';
import {
  getCurrentTeamEventId,
  getPreviousTeamEventId,
  getTeamBracket,
  getTeamEventScore,
  getTeamWeeklyHelps,
} from './team.getters';
import { State } from '../State';

// Computed properties for user's team state.
export const teamProperties = {
  /**
   * Get user's current team id, if any.
   */
  currentTeamId: payloadComputedProperty(
    SB.string().optional(),
    ({ team }: State) => team.currentTeamId,
  ),
};

// Computed properties for team shared state.
export const teamComputedProperties = createSharedStateComputedProperties(
  teamSharedStateSchema,
)({
  teamName: searchableComputedProperty(matchableString(), (state) => {
    return state.global.profile.name;
  }),

  profile: searchableComputedProperty(
    teamProfileSchema,
    (state) => state.global.profile,
  ),

  members: searchableComputedProperty(SB.array(SB.string()), (state) =>
    Object.keys(state.global.members),
  ),

  events: searchableComputedProperty(
    SB.array(
      SB.object({
        eventId: SB.string(),
        type: SB.string(),
        sharedStateId: SB.string(),
        createdAt: SB.number(),
        modifiedAt: SB.number(),
        score: SB.number(),
        targetScore: SB.number().optional(),
      }),
    ),
    (state) =>
      Object.keys(state.global.events).map((eventId) => ({
        ...state.global.events[eventId],
        eventId,
      })),
  ),

  weeklyHelps: searchableComputedProperty(SB.number(), (state, api) => {
    return getTeamWeeklyHelps(state, api.date.now());
  }),

  admins: searchableComputedProperty(SB.array(SB.string()), (state) =>
    Object.keys(state.global.members).filter(
      (userId) => state.global.members[userId].role === 'admin',
    ),
  ),

  memebersCount: searchableComputedProperty(
    SB.number(),
    (state) => Object.keys(state.global.members).length,
  ),

  previousEventScore: searchableComputedProperty(SB.number(), (state, api) => {
    const previousEventId = getPreviousTeamEventId(state, api.date.now());
    if (!previousEventId) return 0;
    return getTeamEventScore(state, previousEventId);
  }),

  currentEventScore: searchableComputedProperty(SB.number(), (state, api) => {
    const currentEventId = getCurrentTeamEventId(api.date.now());
    if (!currentEventId) return 0;
    return getTeamEventScore(state, currentEventId);
  }),

  matchMakingBracket: searchableComputedProperty(SB.string(), (state, api) => {
    return getTeamBracket(state, api.date.now());
  }),

  createdTimestamp: searchableComputedProperty(SB.int(), (state) => {
    return state.global.createdTimestamp;
  }),
});

export type TeamComputedProperties = {
  [T in keyof typeof teamComputedProperties]: SB.ExtractType<
    (typeof teamComputedProperties)[T]['type']
  >;
};
